import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { apiFetch } from '../../api.js';
import { db } from '../../db/local.js';
import { syncOnce } from '../../sync/engine.js';
import type { LocalExercise, LocalPlan, LocalPlanDay, LocalPlanExercise } from '../../db/local.js';
import { listExercises, shortLabel } from '../../db/exercises.js';
import {
  activatePlan,
  addDay,
  addExerciseToDay,
  createPlan,
  deleteDay,
  deletePlan,
  listDayExercises,
  listDays,
  listPlans,
  missingStarterPlans,
  movePlanExercise,
  removePlanExercise,
  renameDay,
  swapPlanExercise,
  updatePlanExercise,
} from '../../db/plans.js';

/**
 * Der Plan-Editor.
 *
 * Ein Plan besteht aus Trainingstagen, ein Trainingstag aus Übungen an einer
 * Position. Sätze und Spanne stehen am Eintrag, nicht an der Übung: „hier 4×5"
 * darf einen anderen Plan nicht mitverändern.
 */
export function PlanEditor(): React.JSX.Element {
  const { t } = useTranslation();
  const nameId = useId();
  const [name, setName] = useState('');
  const [openPlan, setOpenPlan] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const plans = useLiveQuery(() => listPlans(db), [], [] as LocalPlan[]);
  const missing = useLiveQuery(() => missingStarterPlans(db), [], 0);

  return (
    <section aria-labelledby="plans-heading" className="flex flex-col gap-4">
      <header className="flex items-center justify-between gap-3">
        <h2 id="plans-heading" className="text-lg font-semibold">
          {t('training.plans.heading')}
        </h2>
        <button
          type="button"
          onClick={() => setCreating((value) => !value)}
          aria-expanded={creating}
          className="kw-btn kw-btn-small"
        >
          {t('training.plans.new')}
        </button>
      </header>
      <p className="text-sm text-ink-muted">{t('training.plans.intro')}</p>

      {creating ? (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            const trimmed = name.trim();
            if (!trimmed) return;
            void createPlan(trimmed, null, db).then((plan) => setOpenPlan(plan.id));
            setName('');
            setCreating(false);
          }}
        >
          <div className="flex min-w-40 flex-1 flex-col gap-1">
            <label htmlFor={nameId} className="text-xs text-ink-muted">
              {t('training.plans.newPlan')}
            </label>
            <input
              id={nameId}
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={120}
              className="kw-field"
            />
          </div>
          <button type="submit" className="kw-btn kw-btn-small">
            {t('training.plans.create')}
          </button>
        </form>
      ) : null}

      {missing > 0 ? <StarterUpdate count={missing} /> : null}

      {plans.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('training.plans.empty')}</p>
      ) : (
        // Eine schmale Liste wie bei den Übungen (25.10.2026, Timo: „die Knöpfe
        // sind zu groß"): Tippen klappt den Plan auf, „laufend setzen" und
        // „löschen" stehen erst dort — vorher trug jeder Plan drei Knöpfe.
        <ul className="kw-tile flex flex-col divide-y divide-line p-0">
          {plans.map((plan) => {
            const open = openPlan === plan.id;
            return (
              <li key={plan.id}>
                <button
                  type="button"
                  onClick={() => setOpenPlan(open ? null : plan.id)}
                  aria-expanded={open}
                  className="flex min-h-11 w-full items-center gap-3 px-4 py-3 text-left hover:bg-surface-raised focus-visible:bg-surface-raised"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium">{plan.name}</span>
                    {plan.note ? (
                      <span className="block text-xs text-ink-muted">{plan.note}</span>
                    ) : null}
                  </span>
                  {plan.active ? (
                    <span className="shrink-0 rounded-full border border-accent px-2 py-0.5 text-xs text-accent">
                      {t('training.plans.active')}
                    </span>
                  ) : null}
                  <span aria-hidden="true" className="shrink-0 text-ink-muted">
                    {open ? '▾' : '›'}
                  </span>
                </button>

                {open ? (
                  <div className="px-4 pb-4">
                    <div className="flex flex-wrap gap-2">
                      {!plan.active ? (
                        <button
                          type="button"
                          onClick={() => void activatePlan(plan.id, db)}
                          className="kw-btn kw-btn-small px-3"
                        >
                          {t('training.plans.activate')}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => void deletePlan(plan.id, db)}
                        aria-label={t('training.plans.deleteNamed', { name: plan.name })}
                        className="kw-btn kw-btn-quiet kw-btn-small ml-auto px-3 hover:border-danger hover:text-danger"
                      >
                        {t('training.plans.delete')}
                      </button>
                    </div>
                    <PlanDays planId={plan.id} />
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function PlanDays({ planId }: { planId: string }): React.JSX.Element {
  const { t } = useTranslation();
  const dayId = useId();
  const [dayName, setDayName] = useState('');
  const days = useLiveQuery(() => listDays(planId, db), [planId], [] as LocalPlanDay[]);

  return (
    <div className="mt-4 flex flex-col gap-3 border-t border-line pt-4">
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = dayName.trim();
          if (!trimmed) return;
          void addDay(planId, trimmed, db);
          setDayName('');
        }}
      >
        <div className="flex min-w-40 flex-1 flex-col gap-1">
          <label htmlFor={dayId} className="text-xs text-ink-muted">
            {t('training.plans.newDay')}
          </label>
          <input
            id={dayId}
            value={dayName}
            onChange={(event) => setDayName(event.target.value)}
            maxLength={120}
            placeholder={t('training.plans.newDayPlaceholder')}
            className="kw-field"
          />
        </div>
        <button
          type="submit"
          className="rounded-lg border border-line px-3 py-2 text-sm hover:border-accent"
        >
          {t('training.plans.addDay')}
        </button>
      </form>

      {days.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('training.plans.noDays')}</p>
      ) : null}

      <ol className="flex flex-col gap-3">
        {days.map((day) => (
          <li key={day.id}>
            <PlanDayCard day={day} />
          </li>
        ))}
      </ol>
    </div>
  );
}

function PlanDayCard({ day }: { day: LocalPlanDay }): React.JSX.Element {
  const { t } = useTranslation();
  const pickerId = useId();
  const [name, setName] = useState(day.name);

  const entries = useLiveQuery(
    () => listDayExercises(day.id, db),
    [day.id],
    [] as LocalPlanExercise[],
  );
  const exercises = useLiveQuery(() => listExercises(db), [], [] as LocalExercise[]);
  const byId = new Map(exercises.map((exercise) => [exercise.id, exercise]));

  return (
    <div className="rounded-lg border border-line bg-surface-raised p-3">
      <div className="flex items-center justify-between gap-2">
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          onBlur={() => {
            if (name.trim() && name.trim() !== day.name) void renameDay(day.id, name, db);
          }}
          aria-label={t('training.plans.dayName')}
          className="min-w-0 flex-1 rounded border border-transparent bg-transparent px-1 py-1 font-medium hover:border-line focus:border-line"
        />
        <button
          type="button"
          onClick={() => void deleteDay(day.id, db)}
          aria-label={t('training.plans.deleteDayNamed', { name: day.name })}
          className="shrink-0 kw-btn kw-btn-quiet kw-btn-small px-3 hover:border-danger hover:text-danger"
        >
          {t('training.plans.deleteDay')}
        </button>
      </div>

      <ol className="mt-2 flex flex-col gap-2">
        {entries.map((entry, index) => {
          const exercise = byId.get(entry.exerciseId);
          return (
            <li key={entry.id} className="rounded border border-line p-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm">
                    {exercise ? exercise.name : t('training.plans.unknownExercise')}
                  </p>
                  {exercise?.isCompound ? (
                    <p className="text-xs text-ink-muted">{t('training.plans.compound')}</p>
                  ) : null}
                </div>
                <div className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    onClick={() => void movePlanExercise(entry.id, -1, db)}
                    disabled={index === 0}
                    aria-label={t('training.plans.moveUp')}
                    className="kw-btn kw-btn-quiet kw-btn-small px-3 disabled:opacity-40"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => void movePlanExercise(entry.id, 1, db)}
                    disabled={index === entries.length - 1}
                    aria-label={t('training.plans.moveDown')}
                    className="kw-btn kw-btn-quiet kw-btn-small px-3 disabled:opacity-40"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() => void removePlanExercise(entry.id, db)}
                    aria-label={t('training.plans.removeEntry')}
                    className="kw-btn kw-btn-quiet kw-btn-small px-3 hover:border-danger hover:text-danger"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="mt-2 flex flex-wrap items-end gap-2">
                <SmallNumber
                  label={t('training.plans.sets')}
                  value={entry.sets}
                  onChange={(value) => void updatePlanExercise(entry.id, { sets: value }, db)}
                />
                <SmallNumber
                  label={t('training.plans.repMin')}
                  value={entry.repMin}
                  onChange={(value) => void updatePlanExercise(entry.id, { repMin: value }, db)}
                />
                <SmallNumber
                  label={t('training.plans.repMax')}
                  value={entry.repMax}
                  onChange={(value) => void updatePlanExercise(entry.id, { repMax: value }, db)}
                />
                <SmallNumber
                  label={t('training.plans.rest')}
                  value={entry.restSeconds ?? exercise?.restSeconds ?? 90}
                  step={15}
                  onChange={(value) =>
                    void updatePlanExercise(entry.id, { restSeconds: value }, db)
                  }
                />

                {index > 0 ? (
                  <button
                    type="button"
                    aria-pressed={entry.supersetWithPrevious === true}
                    onClick={() =>
                      void updatePlanExercise(
                        entry.id,
                        { supersetWithPrevious: !entry.supersetWithPrevious },
                        db,
                      )
                    }
                    className="kw-toggle"
                  >
                    {t('training.plans.superset')}
                  </button>
                ) : null}

                {/* Tauschen schlägt zuerst die Geschwister vor — der Fall „Bank besetzt". */}
                <SwapPicker entry={entry} exercise={exercise ?? null} exercises={exercises} />
              </div>
            </li>
          );
        })}
      </ol>

      <div className="mt-3 flex flex-col gap-1">
        <label htmlFor={pickerId} className="text-xs text-ink-muted">
          {t('training.plans.addExercise')}
        </label>
        <select
          id={pickerId}
          defaultValue=""
          onChange={(event) => {
            const exercise = byId.get(event.target.value);
            if (exercise) void addExerciseToDay(day.id, exercise, db);
            event.target.value = '';
          }}
          className="kw-field"
        >
          <option value="">{t('training.plans.pickExercise')}</option>
          {exercises.map((exercise) => (
            <option key={exercise.id} value={exercise.id}>
              {exercise.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function SwapPicker({
  entry,
  exercise,
  exercises,
}: {
  entry: LocalPlanExercise;
  exercise: LocalExercise | null;
  exercises: LocalExercise[];
}): React.JSX.Element {
  const { t } = useTranslation();
  const id = useId();

  const rootId = exercise?.parentId ?? exercise?.id ?? null;
  const siblings = exercises.filter(
    (candidate) =>
      candidate.id !== entry.exerciseId &&
      rootId !== null &&
      (candidate.id === rootId || candidate.parentId === rootId),
  );
  const others = exercises.filter(
    (candidate) =>
      candidate.id !== entry.exerciseId && !siblings.some((sibling) => sibling.id === candidate.id),
  );

  return (
    <label htmlFor={id} className="flex min-w-40 flex-1 flex-col gap-1 text-xs text-ink-muted">
      {t('training.plans.swap')}
      <select
        id={id}
        defaultValue=""
        onChange={(event) => {
          if (event.target.value) void swapPlanExercise(entry.id, event.target.value, db);
          event.target.value = '';
        }}
        className="kw-field text-ink"
      >
        <option value="">{t('training.plans.swapPlaceholder')}</option>
        {siblings.length > 0 ? (
          <optgroup label={t('training.plans.sameFamily')}>
            {siblings.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {shortLabel(candidate)}
              </option>
            ))}
          </optgroup>
        ) : null}
        <optgroup label={t('training.plans.allExercises')}>
          {others.map((candidate) => (
            <option key={candidate.id} value={candidate.id}>
              {candidate.name}
            </option>
          ))}
        </optgroup>
      </select>
    </label>
  );
}

function SmallNumber({
  label,
  value,
  step,
  onChange,
}: {
  label: string;
  value: number;
  step?: number;
  onChange: (value: number) => void;
}): React.JSX.Element {
  const id = useId();
  return (
    <label htmlFor={id} className="flex w-20 flex-col gap-1 text-xs text-ink-muted">
      {label}
      <input
        id={id}
        type="number"
        min={0}
        step={step ?? 1}
        value={value}
        onChange={(event) => {
          const parsed = Number(event.target.value);
          if (Number.isFinite(parsed)) onChange(parsed);
        }}
        className="rounded-lg border border-line bg-surface px-2 py-2 text-base text-ink"
      />
    </label>
  );
}

/**
 * Vorlagen, die nach dem Anlegen des Kontos dazukamen, holt der Server ins
 * Konto (`POST /account/starter`); der Abgleich bringt sie danach aufs Gerät.
 */
function StarterUpdate({ count }: { count: number }): React.JSX.Element {
  const { t } = useTranslation();
  const [state, setState] = useState<'idle' | 'busy' | 'failed'>('idle');

  async function fetchTemplates() {
    setState('busy');
    try {
      await apiFetch('/account/starter', { method: 'POST', body: '{}' });
      const result = await syncOnce();
      setState(result.error ? 'failed' : 'idle');
    } catch {
      setState('failed');
    }
  }

  return (
    <div className="kw-tile flex flex-col gap-2">
      <p className="text-sm">{t('training.plans.starterNew', { count })}</p>
      <button
        type="button"
        onClick={() => void fetchTemplates()}
        disabled={state === 'busy'}
        className="kw-btn kw-btn-small self-start"
      >
        {t('training.plans.starterFetch')}
      </button>
      {state === 'failed' ? (
        <p role="alert" className="text-sm text-danger">
          {t('training.plans.starterFailed')}
        </p>
      ) : null}
    </div>
  );
}
